import type { UploadProgress } from '../types/UploadProgress';

export interface PutToS3Options {
  /** Pre-signed PUT URL returned by the media-gallery service. */
  uploadUrl: string;
  /** The browser `File` or `Blob` to send as the request body. */
  file: Blob;
  /**
   * Content-Type header to send. Defaults to `file.type` and falls
   * back to `application/octet-stream` if the browser didn't sniff
   * the file. S3 stores this verbatim on the object metadata.
   */
  contentType?: string;
  /**
   * Fires for each `XMLHttpRequest.upload.progress` event. Receives
   * normalized progress. The final tick at completion is synthesized
   * with `ratio: 1` even if S3 didn't send a length-computable
   * progress event for the last chunk.
   */
  onProgress?: (progress: UploadProgress) => void;
  /**
   * Aborts the in-flight PUT. Throws `DOMException('Aborted',
   * 'AbortError')` from the returned promise so callers can branch
   * cleanly on `error.name === 'AbortError'`.
   */
  signal?: AbortSignal;
}

/**
 * Single-PUT upload primitive. Streams the file body to a pre-signed
 * S3 URL via `XMLHttpRequest` — `fetch` lacks request-progress events
 * in modern browsers, so XHR remains the only viable transport when
 * we want to surface upload progress to UI.
 *
 * Mirrors PopshopRN's `uploadToS3PreSignedUrl` (axios-based on RN; XHR
 * here for parity with the existing web `uploadMedia` primitive and
 * to avoid pulling axios into `@ps/features`).
 *
 * Pure I/O — no React, no state. Compose inside service hooks, send
 * pipelines, batch loops, etc.
 */
export function putToS3(options: PutToS3Options): Promise<void> {
  const { uploadUrl, file, onProgress, signal } = options;
  const contentType =
    options.contentType ?? file.type ?? 'application/octet-stream';

  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload was aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = event => {
      if (!event.lengthComputable || !onProgress) return;
      const total = event.total;
      const loaded = event.loaded;
      const ratio = total > 0 ? Math.min(1, loaded / total) : 0;
      onProgress({ loaded, total, ratio });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Synthesize a final tick so consumers that only react to
        // `ratio === 1` get a deterministic completion signal even
        // when S3's last progress event didn't reach 100%.
        onProgress?.({ loaded: file.size, total: file.size, ratio: 1 });
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () =>
      reject(new DOMException('Upload was aborted', 'AbortError'));

    if (signal) {
      const onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort, { once: true });
    }

    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
}
